import {useCallback, useEffect, useState} from 'react';

const PODCAST_API_URL = 'https://gateway.lavita.ai/user-service/notification/inbox';
const LOGIN_API_URL = 'https://gateway.lavita.ai/user-service/login';
const PODCAST_STREAM_API_URL = 'https://gateway.lavita.ai/user-service/podcasts/stream';

const parseTimestamp = (value) => {
    if (typeof value === 'number') {
        return value;
    }

    const parsed = new Date(value).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
};

const createStreamUrl = (item, format) => {
    const params = new URLSearchParams({
        code: item.code || '',
        topic: item.topic || '',
        title: item.title || '',
        format,
    });

    return `${PODCAST_STREAM_API_URL}?${params.toString()}`;
};

const normalizePodcast = (item) => ({
    id: item.messageId || item.inboxId || item.code,
    title: item.title || 'Untitled episode',
    topic: item.topic || 'Lavita AI',
    content: item.content || '',
    createdAt: parseTimestamp(item.createdAt),
    artworkRequestUrl: createStreamUrl(item, 'image'),
    audioRequestUrl: createStreamUrl(item, 'audio'),
});

const sortNewestFirst = (items) => (
    [...items].sort((first, second) => second.createdAt - first.createdAt)
);

const previewEpisodes = [
    {messageId: 'preview-1', code: 'preview-1', topic: 'Health Innovators', title: 'The future of AI-enabled health discovery', content: 'A conversation on responsible AI, research collaboration, and the future of patient-centered care.', createdAt: Date.UTC(2026, 0, 20)},
    {messageId: 'preview-2', code: 'preview-2', topic: 'Lavita AI', title: 'Building privacy into the data layer', content: 'How user-owned health data can support better research without compromising individual control.', createdAt: Date.UTC(2025, 11, 21)},
    {messageId: 'preview-3', code: 'preview-3', topic: 'Health Innovators', title: 'A conversation with the builders of next-generation care', content: 'Exploring the people and infrastructure behind a more connected healthcare ecosystem.', createdAt: Date.UTC(2025, 10, 3)},
];

const createAuthHeaders = (session) => ({
    Authorization: session.bearerToken,
    ...(session.refreshBearerToken ? {RefreshBearerToken: session.refreshBearerToken} : {}),
});

const loginWithPublicAccount = async (signal) => {
    const username = process.env.REACT_APP_PODCAST_USERNAME;
    const password = process.env.REACT_APP_PODCAST_PASSWORD;

    if (!username || !password) {
        throw new Error('Podcast public account has not been configured');
    }

    const response = await fetch(LOGIN_API_URL, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
            username,
            password: password.split(''),
            token: '',
            loginSource: 1,
            biometrics: false,
        }),
        signal,
    });

    const payload = await response.json();
    const responseData = payload?.data || {};
    const bearerToken = response.headers.get('bearertoken')
        || response.headers.get('authorization')
        || responseData.bearerToken
        || responseData.BearerToken
        || responseData.token;

    if (!response.ok) {
        throw new Error(payload?.message || 'Podcast public account login failed');
    }

    if (!bearerToken) {
        throw new Error('Login succeeded, but the browser could not read the Bearer token. The API must expose BearerToken through CORS.');
    }

    return {
        bearerToken,
        refreshBearerToken: response.headers.get('refreshbearertoken') || responseData.refreshBearerToken,
        userId: responseData.userId,
    };
};

const usePodcasts = () => {
    const [podcasts, setPodcasts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [session, setSession] = useState(null);

    const fetchPodcasts = useCallback(async (signal) => {
        setLoading(true);
        setError(null);

        try {
            if (new URLSearchParams(window.location.search).has('preview')) {
                setPodcasts(previewEpisodes.map(normalizePodcast));
                return;
            }

            const authenticatedSession = await loginWithPublicAccount(signal);
            setSession(authenticatedSession);
            const response = await fetch(PODCAST_API_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...createAuthHeaders(authenticatedSession),
                },
                body: JSON.stringify({
                    userId: authenticatedSession.userId,
                    type: 'podcasts',
                    pageSize: 20,
                }),
                signal,
            });

            if (!response.ok) {
                throw new Error(`Podcast request failed with ${response.status}`);
            }

            const payload = await response.json();
            const list = payload?.data?.list;

            if (!Array.isArray(list)) {
                throw new Error('Podcast response did not include an episode list');
            }

            setPodcasts(sortNewestFirst(list.map(normalizePodcast)));
        } catch (requestError) {
            if (requestError.name !== 'AbortError') {
                setError(requestError.message || 'Unable to load podcasts right now. Please try again.');
            }
        } finally {
            if (!signal?.aborted) {
                setLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        fetchPodcasts(controller.signal);

        return () => controller.abort();
    }, [fetchPodcasts]);

    const refresh = useCallback(() => fetchPodcasts(), [fetchPodcasts]);

    const fetchPodcastAsset = useCallback(async (requestUrl, signal) => {
        if (!session) {
            throw new Error('Podcast session is unavailable');
        }

        const response = await fetch(requestUrl, {
            headers: createAuthHeaders(session),
            signal,
        });

        if (!response.ok) {
            throw new Error('Podcast media request failed');
        }

        return URL.createObjectURL(await response.blob());
    }, [session]);

    return {podcasts, loading, error, refresh, fetchPodcastAsset};
};

export default usePodcasts;
